import { Injectable, inject } from '@angular/core';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import isObject from 'lodash-es/isObject';
import omitBy from 'lodash-es/omitBy';

@Injectable()
export class ProductConfigurationConfigurableStorageService {
    /**
     * Services
     */
    private hashStorageService = inject(HashStorageService);
    private moduleConfig = inject(NProductConfiguration.OPTIONS);
    private attributeResource = inject(AttributeResource);

    getHashParams(selection: NProductConfiguration.Configurable.Selection, logic?: NProductConfiguration.Configurable.THashStorageLogic) {
        const params = omitBy(selection, (value, key) => {
            return value === undefined;
        }) as {
            [attributeCode: string]: number | string | undefined;
        };
        if (logic === 'label') {
            Object.keys(params).forEach(key => {
                const option = params[key];
                if (option) {
                    const attribute = this.attributeResource.getExisting(key);
                    if (attribute) {
                        const optionLabel = attribute.getOptionLabel(option);
                        if (!Array.isArray(optionLabel)) {
                            params[key] = optionLabel;
                        }
                    }
                }
            });
        }
        return params;
    }

    /**
     * Write selection to hash storage
     */
    write(
        selection: NProductConfiguration.Configurable.Selection,
        hashKey?: string | false,
        logic?: NProductConfiguration.Configurable.THashStorageLogic
    ) {
        if (!hashKey) {
            return;
        }
        this.hashStorageService.set(hashKey, this.getHashParams(selection, logic));
    }

    /**
     * Read selection from hash storage
     */
    read(
        hashKey?: string | false,
        logic?: NProductConfiguration.Configurable.THashStorageLogic,
        product?: Product<IProductDataV3>
    ): NProductConfiguration.Configurable.Selection | undefined {
        if (!hashKey) {
            return undefined;
        }

        const selection = this.hashStorageService.get(hashKey);
        if (!selection || !isObject(selection)) {
            return undefined;
        }
        if (logic === 'label') {
            const parent = product?.parent as Product<IProductDataV3>;
            Object.keys(selection).forEach(key => {
                const attribute = this.attributeResource.getExisting(key);
                const label = selection[key];

                if (attribute) {
                    const parentOptionsValues = Object.keys(parent?.data?.options?.configurable?.[key] || {})
                        .map(i => parseInt(i))
                        .filter(i => !!i);
                    const parentOptions = attribute.data?.options?.filter(i => parentOptionsValues.includes(i.value));
                    const value = attribute.getOptionValue(label, parentOptions?.length ? parentOptions : undefined);
                    if (value) {
                        selection[key] = value;
                    }
                }
            });
        }

        const cleanedSelection = omitBy(selection, (value, key) => {
            return typeof value !== 'number' || typeof key !== 'string';
        });

        return cleanedSelection as NProductConfiguration.Configurable.Selection;
    }
}
